/**
 * CR481: Card Snippet configuration types and defaults.
 * CR483: Extended extractSnippetMeta to resolve FK-based snippet_artifact_id path.
 * Defines eligibility rules, fallback behavior, and presentation knobs.
 */

// ================================================================
// CONFIG TYPES
// ================================================================

export type BboxQualityLevel = "tight" | "medium" | "loose" | "bad";
export type FallbackMode = "none" | "brand_artwork" | "issuer_artwork" | "generic_placeholder";
export type CornerRadius = "none" | "sm" | "md" | "lg";
export type BorderStyle = "subtle" | "strong";
export type AspectRatio = "3:2" | "4:3" | "1:1";

export interface CardSnippetConfig {
  enabled: boolean;

  // 1) Eligibility
  require_valid_snippet: boolean;
  min_bbox_quality: BboxQualityLevel;
  allow_loose_if_only_option: boolean;

  // 2) Fallback behavior
  fallback_mode: FallbackMode;

  // 3) Bad snippet handling
  hide_if_bad_bbox: boolean;
  show_warning_bad_bbox: boolean; // admin-only

  // 4) Presentation
  corner_radius: CornerRadius;
  show_border: boolean;
  border_style: BorderStyle;
  aspect_ratio: AspectRatio;

  // 5) Labels / badges
  show_snippet_badge: boolean;
  snippet_badge_text: string | null;
}

export const DEFAULT_CARD_SNIPPET_CONFIG: CardSnippetConfig = {
  enabled: true,
  require_valid_snippet: true,
  min_bbox_quality: "medium",
  allow_loose_if_only_option: true,
  fallback_mode: "brand_artwork",
  hide_if_bad_bbox: true,
  show_warning_bad_bbox: true,
  corner_radius: "lg",
  show_border: true,
  border_style: "subtle",
  aspect_ratio: "4:3",
  show_snippet_badge: true,
  snippet_badge_text: "From flyer",
};

// ================================================================
// SNIPPET META (expected shape from deal payload)
// ================================================================

/** CR486: Locator status taxonomy */
export type SnippetLocatorStatus =
  | "eligible"
  | "accepted"
  | "missing"
  | "invalid"
  | "oversized"
  | "not_localizable"
  // CR486 new statuses
  | "locator_ok"
  | "locator_model_failed"
  | "locator_no_match_on_page"
  | "locator_ambiguous_multiple_panels"
  | "locator_bbox_full_page"
  | "locator_bbox_oversized"
  | "locator_missing_image"
  | "locator_heuristic_bbox";

/** CR486: Locator reject reason taxonomy */
export type SnippetRejectReason =
  | "none"
  | "rejected_bbox_oversized"
  | "rejected_bbox_full_page"
  | "rejected_bbox_invalid"
  | "not_localizable"
  | "quality_gate_loose"
  // CR486 new reasons
  | "MISSING_PAGE_IMAGE_URL"
  | "AI_CALL_FAILED"
  | "NO_REGION_ABOVE_THRESHOLD"
  | "AMBIGUOUS_PANELS";

/** CR486: Scoring breakdown for snippet locator decisions */
export interface BboxScoreComponents {
  key_phrase_hits: number;
  area_penalty: number;
  overlap_penalty: number;
}

export interface FlyerSnippetMeta {
  snippet_status: SnippetLocatorStatus | null;
  bbox_quality_hint: "tight" | "medium" | "loose" | "bad" | null;
  bbox_area_ratio: number | null;
  bbox_reject_reason: SnippetRejectReason | null;
  snippet_asset_path: string | null;
  render_source: string | null;
  /** CR483: FK to deal_artifacts.id (when using FK-based provenance) */
  snippet_artifact_id?: string | null;
  /** CR486: Final locator score used to choose this bbox */
  bbox_score?: number | null;
  /** CR486: Score breakdown components */
  bbox_score_components?: BboxScoreComponents | null;
  /** CR486: Which locator pass produced this bbox */
  bbox_locator_pass?: "model_full_page" | "model_guided" | "heuristic_only" | null;
  /** CR486: Short reason string summarizing the decision */
  locator_reason?: string | null;
}

// ================================================================
// ELIGIBILITY LOGIC
// ================================================================

const QUALITY_RANK: Record<string, number> = { tight: 3, medium: 2, loose: 1, bad: 0 };

/**
 * Determine if a snippet is eligible for display on a card.
 * Returns { eligible, reason }.
 */
export function evaluateSnippetEligibility(
  meta: FlyerSnippetMeta | null | undefined,
  config: CardSnippetConfig,
  isAdmin: boolean,
): { eligible: boolean; reason: string } {
  if (!meta) return { eligible: false, reason: "no_snippet_meta" };

  // No asset path → not eligible
  if (!meta.snippet_asset_path) return { eligible: false, reason: "no_asset_path" };

  // Status check
  const goodStatuses = ["eligible", "accepted"];
  if (config.require_valid_snippet && meta.snippet_status && !goodStatuses.includes(meta.snippet_status)) {
    // Admin can still see if require_valid_snippet is relaxed
    if (!isAdmin) return { eligible: false, reason: `status_${meta.snippet_status}` };
  }

  // Reject reason check
  const hasRejectReason = meta.bbox_reject_reason && meta.bbox_reject_reason !== "none";
  if (hasRejectReason) {
    if (config.hide_if_bad_bbox) return { eligible: false, reason: `rejected_${meta.bbox_reject_reason}` };
    // Live never shows rejected
    if (!isAdmin) return { eligible: false, reason: `rejected_live_${meta.bbox_reject_reason}` };
  }

  // Quality check
  const quality = meta.bbox_quality_hint ?? "bad";
  const qualityRank = QUALITY_RANK[quality] ?? 0;
  const minRank = QUALITY_RANK[config.min_bbox_quality] ?? 2;

  if (qualityRank < minRank) {
    // Allow loose if it's the only option
    if (quality === "loose" && config.allow_loose_if_only_option) {
      return { eligible: true, reason: "loose_only_option" };
    }
    return { eligible: false, reason: `quality_${quality}_below_${config.min_bbox_quality}` };
  }

  return { eligible: true, reason: "ok" };
}

/**
 * Extract FlyerSnippetMeta from a deal payload (handles various shapes).
 * CR483: Also resolves FK-based snippet_artifact references.
 */
export function extractSnippetMeta(deal: any): FlyerSnippetMeta | null {
  // Direct meta (highest priority)
  if (deal?.flyer_snippet_meta) return deal.flyer_snippet_meta;

  // CR483: Resolved snippet artifact (FK path — joined data)
  if (deal?.snippet_artifact) {
    const sa = deal.snippet_artifact;
    const payload = sa.payload ?? {};
    return {
      snippet_status: sa.snippet_status ?? sa.status ?? payload.snippet_status ?? null,
      bbox_quality_hint: sa.bbox_quality_hint ?? payload.bbox_quality_hint ?? null,
      bbox_area_ratio: sa.bbox_area_ratio ?? payload.bbox_area_ratio ?? null,
      bbox_reject_reason: sa.bbox_reject_reason ?? payload.bbox_reject_reason ?? null,
      snippet_asset_path: sa.snippet_asset_path ?? sa.storage_path ?? payload.snippet_asset_path ?? null,
      render_source: sa.render_source ?? payload.render_source ?? null,
      snippet_artifact_id: sa.id ?? deal.snippet_artifact_id ?? null,
      bbox_score: sa.bbox_score ?? payload.bbox_score ?? null,
      bbox_score_components: sa.bbox_score_components ?? payload.bbox_score_components ?? null,
      bbox_locator_pass: sa.bbox_locator_pass ?? payload.bbox_locator_pass ?? null,
      locator_reason: sa.locator_reason ?? payload.locator_reason ?? null,
    };
  }

  // Build from flyer_artifact (legacy shape)
  const fa = deal?.flyer_artifact ?? deal?.flyerArtifact ?? deal?.flyer ?? null;
  if (!fa) {
    // CR483: Check for bare snippet_artifact_id without joined data
    // This signals the FK exists but wasn't joined — return minimal meta
    if (deal?.snippet_artifact_id) {
      return {
        snippet_status: null,
        bbox_quality_hint: null,
        bbox_area_ratio: null,
        bbox_reject_reason: null,
        snippet_asset_path: null,
        render_source: null,
        snippet_artifact_id: deal.snippet_artifact_id,
      };
    }
    return null;
  }

  return {
    snippet_status: fa.snippet_status ?? fa.status ?? null,
    bbox_quality_hint: fa.bbox_quality_hint ?? null,
    bbox_area_ratio: fa.bbox_area_ratio ?? fa.area_ratio ?? null,
    bbox_reject_reason: fa.bbox_reject_reason ?? null,
    snippet_asset_path: fa.snippet_asset_path ?? fa.snippet_asset_path_norm ?? null,
    render_source: fa.render_source ?? null,
    snippet_artifact_id: fa.snippet_artifact_id ?? deal.snippet_artifact_id ?? null,
    bbox_score: fa.bbox_score ?? null,
    bbox_score_components: fa.bbox_score_components ?? null,
    bbox_locator_pass: fa.bbox_locator_pass ?? null,
    locator_reason: fa.locator_reason ?? null,
  };
}

// ================================================================
// PRESENTATION HELPERS
// ================================================================

export function getCornerRadiusClass(radius: CornerRadius): string {
  switch (radius) {
    case "none": return "rounded-none";
    case "sm": return "rounded-sm";
    case "md": return "rounded-lg";
    case "lg": return "rounded-xl";
    default: return "rounded-lg";
  }
}

export function getBorderClass(show: boolean, style: BorderStyle): string {
  if (!show) return "border-0";
  return style === "strong" ? "border-2 border-border" : "border border-border/50";
}

export function getAspectClass(ratio: AspectRatio): string {
  switch (ratio) {
    case "3:2": return "aspect-[3/2]";
    case "4:3": return "aspect-[4/3]";
    case "1:1": return "aspect-square";
    default: return "aspect-[4/3]";
  }
}
